import React from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';
import { TemplateField, SectionFieldOption } from '../../../_shared/_types';
import { BaseDateInput, BaseTimeInput, BaseNumberInput } from 'views/App/Portal/PerformanceReporting/_components/FieldInputs';
import { parseTimeValue } from 'views/App/Portal/PerformanceReporting/_utils/fieldInputHelpers';

interface TemplateFieldPreviewProps {
  field: TemplateField;
}

const TemplateFieldPreview: React.FC<TemplateFieldPreviewProps> = ({ field }) => {
  const fieldType = field.fieldType || 'Text';
  const defaultValue = field.effectiveDefaultValue || '';
  const label = field.effectiveLabel || field.name;

  // Helper to check if an option is valid for display
  const isValidOption = (opt: SectionFieldOption): boolean => {
    return opt.status === 'Active' && !opt.effectiveIsDeleted && !!opt.effectiveLabel && opt.effectiveLabel.trim() !== '';
  };

  // Get visible options for SingleSelect
  const getVisibleOptions = (): SectionFieldOption[] => {
    if (!field.sectionFieldOptions) return [];
    return field.sectionFieldOptions.filter(isValidOption);
  };

  switch (fieldType) {
    case 'Text':
    case 'Lookup':
      return (
        <TextField
          label={label}
          value={defaultValue}
          disabled={true}
          fullWidth
          size='medium'
          variant='outlined'
        />
      );

    case 'Number':
      return (
        <BaseNumberInput
          value={defaultValue}
          onChange={() => {}}
          label={label}
          size='medium'
          fullWidth={true}
          disabled={true}
        />
      );

    case 'Date':
      return (
        <BaseDateInput
          value={defaultValue}
          onChange={() => {}}
          label={label}
          size='medium'
          fullWidth={true}
          disabled={true}
        />
      );

    case 'Time':
      const timeValue = defaultValue ? parseTimeValue(defaultValue) : null;
      return (
        <BaseTimeInput
          value={timeValue}
          onChange={() => {}}
          label={label}
          size='medium'
          fullWidth={true}
          showTimezone={true}
          disabled={true}
        />
      );

    case 'Boolean':
      const booleanValue = defaultValue === 'true' || defaultValue === 'True';
      return (
        <FormControl fullWidth disabled={true}>
          {label && (
            <Typography
              variant='body2'
              sx={{ mb: 1, color: 'text.secondary' }}
            >
              {label}
            </Typography>
          )}
          <RadioGroup
            value={booleanValue ? 'yes' : 'no'}
            row
          >
            <FormControlLabel
              value='yes'
              control={<Radio />}
              label='Yes'
            />
            <FormControlLabel
              value='no'
              control={<Radio />}
              label='No'
            />
          </RadioGroup>
        </FormControl>
      );

    case 'SingleSelect':
      const visibleOptions = getVisibleOptions();
      return (
        <FormControl
          fullWidth
          size='small'
        >
          <InputLabel id={`select-label-${field.id}`}>{label}</InputLabel>
          <Select
            labelId={`select-label-${field.id}`}
            value={defaultValue || ''}
            label={label}
            disabled={true}
          >
            {visibleOptions.map((option, index) => (
              <MenuItem
                key={option.id || index}
                value={option.effectiveLabel}
              >
                {option.effectiveLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    default:
      return (
        <TextField
          label={label}
          value={defaultValue}
          disabled={true}
          fullWidth
          size='small'
          variant='outlined'
        />
      );
  }
};

export default TemplateFieldPreview;

